import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductFilters from '../components/ProductFilters';
import ProductSort from '../components/ProductSort';
import ProductGrid from '../components/ProductGrid';
import { useProducts, useCategories, useSubcategories, useBrands } from '../hooks/useProducts';
import { Search, ChevronRight } from 'lucide-react';

const ProductsPage: React.FC = () => {
  const { category, subcategory } = useParams<{ category?: string; subcategory?: string }>();
  const [sortOption, setSortOption] = useState('featured');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500000]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Reset checkbox filters when URL parameters change
  useEffect(() => {
    setSelectedSubcategories([]);
    setSelectedBrands([]);
    setSelectedProductTypes([]);
    setSearchQuery('');
  }, [category, subcategory]);
  
  // Fetch data
  const { categories } = useCategories();
  const { subcategories } = useSubcategories(category);
  const { brands } = useBrands();
  
  // Determine subcategory filter: prioritize checkbox selections, fallback to URL parameter
  const subcategoryFilter = selectedSubcategories.length > 0 ? selectedSubcategories : subcategory;
  
  const { products, loading, error } = useProducts({ 
    category,
    subcategory: subcategoryFilter,
    brand: selectedBrands.length === 1 ? selectedBrands[0] : undefined,
    search: searchQuery || undefined
  });
  
  // Get current category details
  const currentCategory = category 
    ? categories.find(c => c.id === category) 
    : { name: 'All Products', id: 'all' };
    
  // Get current subcategory details
  const currentSubcategory = subcategory 
    ? subcategories.find(s => s.id === subcategory)
    : null;
  
  // Update document title based on category and subcategory
  useEffect(() => {
    let title = 'All Products | Strum Music';
    if (currentSubcategory) {
      title = `${currentSubcategory.name} | Strum Music`;
    } else if (currentCategory && currentCategory.id !== 'all') {
      title = `${currentCategory.name} | Strum Music`;
    }
    document.title = title;
  }, [currentCategory, currentSubcategory]);

  // Filter and sort products
  const filteredProducts = React.useMemo(() => {
    let result = [...products];
    
    // Filter by brands if selected
    if (selectedBrands.length > 0) {
      result = result.filter(product => product.brand_id && selectedBrands.includes(product.brand_id));
    }
    
    // Filter by product types if selected
    if (selectedProductTypes.length > 0) {
      result = result.filter(product => selectedProductTypes.includes(product.product_type));
    }
    
    // Filter by price range
    result = result.filter(product => {
      const price = product.price;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Sort products
    switch (sortOption) {
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'featured':
      default:
        result.sort((a, b) => {
          if (a.is_featured === b.is_featured) {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          }
          return a.is_featured ? -1 : 1;
        });
        break;
    }
    
    return result;
  }, [products, selectedBrands, selectedProductTypes, priceRange, sortOption]);
  
  const handleSubcategoryChange = (subcategoryId: string) => {
    setSelectedSubcategories(prev => 
      prev.includes(subcategoryId)
        ? prev.filter(id => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const handleBrandChange = (brandId: string) => {
    setSelectedBrands(prev => 
      prev.includes(brandId)
        ? prev.filter(id => id !== brandId)
        : [...prev, brandId]
    );
  };

  const handleProductTypeChange = (productType: string) => {
    setSelectedProductTypes(prev => 
      prev.includes(productType)
        ? prev.filter(type => type !== productType)
        : [...prev, productType]
    );
  };
  
  const clearFilters = () => {
    setSelectedSubcategories([]);
    setSelectedBrands([]);
    setSelectedProductTypes([]);
    setPriceRange([0, 500000]);
    setSortOption('featured');
    setSearchQuery('');
  };


  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Banner */}
      <div className="bg-white py-12">
        <div className="container-custom">
          {/* Breadcrumbs */}
          <nav className="flex text-sm mb-4">
            <Link to="/" className="text-gray-500 hover:text-red-700">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
            <Link to="/products" className="text-gray-500 hover:text-red-700">Products</Link>
            {category && (
              <>
                <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
                <Link to={`/products/${category}`} className="text-gray-500 hover:text-red-700">
                  {currentCategory?.name}
                </Link>
              </>
            )}
            {subcategory && (
              <>
                <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
                <span className="text-gray-700 font-medium">
                  {currentSubcategory?.name}
                </span>
              </>
            )}
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-black">
            {currentSubcategory?.name || currentCategory?.name || 'All Products'}
          </h1>
          
          {currentCategory?.description && !currentSubcategory && (
            <p className="text-gray-600 max-w-2xl mt-2">
              {currentCategory.description}
            </p>
          )}
          
          {currentSubcategory?.description && (
            <p className="text-gray-600 max-w-2xl mt-2">
              {currentSubcategory.description}
            </p>
          )}
        </div>
      </div>

      {/* Subcategory Navigation - Only show if we have a category but no specific subcategory */}
      {category && !subcategory && subcategories.length > 0 && (
        <div className="bg-gray-50 py-6 border-b border-gray-200">
          <div className="container-custom">
            <div className="flex flex-wrap gap-3">
              <Link
                to={`/products/${category}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  !subcategory
                    ? 'bg-red-700 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                }`}
              >
                View All {currentCategory?.name}
              </Link>
              {subcategories.map((subcat) => (
                <Link
                  key={subcat.id}
                  to={`/products/${category}/${subcat.id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    subcategory === subcat.id
                      ? 'bg-red-700 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                  }`}
                >
                  {subcat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      
      <div className="container-custom py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input 
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700 transition-colors"
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Filters Sidebar */}
          <aside className="w-full lg:w-64 flex-shrink-0 lg:mr-8">
            <ProductFilters
              category={category}
              subcategory={subcategory}
              selectedBrands={selectedBrands}
              selectedSubcategories={selectedSubcategories}
              selectedProductTypes={selectedProductTypes}
              priceRange={priceRange}
              onBrandChange={handleBrandChange}
              onSubcategoryChange={handleSubcategoryChange}
              onProductTypeChange={handleProductTypeChange}
              onPriceRangeChange={setPriceRange}
              onClearFilters={clearFilters}
              showMobileFilters={showFilters}
              onToggleMobileFilters={() => setShowFilters(!showFilters)}
            />
          </aside>
          
          {/* Products Grid */}
          <div className="flex-grow">
            {/* Sort Controls */}
            <ProductSort
              sortOption={sortOption}
              onSortChange={setSortOption}
              productCount={filteredProducts.length}
              currentCategory={currentCategory?.name}
              currentSubcategory={currentSubcategory?.name}
            />
            
            {/* Products Grid */}
            <ProductGrid
              products={filteredProducts}
              loading={loading}
              error={error}
              emptyMessage={`No products found${
                currentSubcategory ? ` in ${currentSubcategory.name}` :
                currentCategory && currentCategory.id !== 'all' ? ` in ${currentCategory.name}` : ''
              }${
                (selectedSubcategories.length > 0 || selectedBrands.length > 0 || selectedProductTypes.length > 0 || searchQuery) 
                  ? ' matching your filters' : ''
              }.`}
              onClearFilters={clearFilters}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;